import { createFileRoute, Link } from "@tanstack/react-router";
import { ArticleLayout } from "@/components/ArticleLayout";
import { RegionalHubLinks } from "@/components/RegionalHubLinks";
import { PricingTeaserStrip } from "@/components/PricingTeaserStrip";
import { seoHead } from "@/components/hubs/HubPages";
import {
  SITE_URL, SCHEMA_LOCALBUSINESS_ID, BUSINESS_NAME, BUSINESS_PHONE_E164, BUSINESS_PHONE_DISPLAY, BUSINESS_PHONE_TEL,
  BUSINESS_STREET, BUSINESS_LOCALITY, BUSINESS_REGION, BUSINESS_POSTAL, BUSINESS_COUNTRY, BUSINESS_ADDRESS_FULL,
} from "@/lib/entitySchema";

const PATH = "/colorado-springs-headquarters";
const TITLE = "Colorado Springs Web Design & SEO Company | 60-Mile Service Area";
const DESC = "Colorado Web Impressions is based in Colorado Springs and serves businesses within 60 miles: El Paso, Teller, Douglas, Pueblo and Fremont counties. Websites, SEO, Google Maps and AI search.";

const COUNTIES: { name: string; towns: { label: string; to?: string }[] }[] = [
  { name: "El Paso County", towns: [
    { label: "Colorado Springs", to: "/colorado-springs" }, { label: "Monument", to: "/monument" }, { label: "Falcon", to: "/falcon" }, { label: "Peyton", to: "/web-design-seo-peyton" }, { label: "Calhan", to: "/web-design-seo-calhan" }, { label: "Ellicott", to: "/web-design-seo-ellicott" },
    { label: "Black Forest", to: "/black-forest" }, { label: "Fountain", to: "/fountain" }, { label: "Security-Widefield", to: "/security-widefield" },
    { label: "Manitou Springs", to: "/manitou-springs" },
  ] },
  { name: "Teller County", towns: [{ label: "Woodland Park", to: "/woodland-park" }, { label: "Divide" }, { label: "Cripple Creek" }] },
  { name: "Douglas County", towns: [{ label: "Castle Rock", to: "/castle-rock" }, { label: "Parker", to: "/parker" }, { label: "Larkspur", to: "/web-design-seo-larkspur" }, { label: "Castle Pines", to: "/web-design-seo-castle-pines" }, { label: "Lone Tree", to: "/web-design-seo-lone-tree" }] },
  { name: "Pueblo County", towns: [{ label: "Pueblo", to: "/pueblo" }, { label: "Pueblo West", to: "/web-design-seo-pueblo-west" }, { label: "Colorado City", to: "/web-design-seo-colorado-city" }] },
  { name: "Fremont County", towns: [{ label: "Cañon City" }, { label: "Florence" }] },
];

const SERVICES = [
  { label: "Colorado Springs web design", to: "/colorado-springs-web-design" },
  { label: "Colorado Springs SEO", to: "/colorado-springs-seo" },
  { label: "Google Maps ranking", to: "/colorado-springs-google-maps" },
  { label: "AI search visibility", to: "/colorado-springs-geo-ai" },
  { label: "Google Ads management", to: "/colorado-springs-ppc" },
  { label: "Pikes Peak contractor marketing", to: "/pikes-peak-contractor-marketing" },
];

const ALL_TOWNS = COUNTIES.flatMap((c) => c.towns.map((t) => t.label));

const SCHEMA = [
  {
    "@context": "https://schema.org",
    "@type": "ProfessionalService",
    "@id": `${SITE_URL}${PATH}#hq`,
    name: `${BUSINESS_NAME} — Colorado Springs Headquarters`,
    url: `${SITE_URL}${PATH}`,
    telephone: BUSINESS_PHONE_E164,
    parentOrganization: { "@id": SCHEMA_LOCALBUSINESS_ID },
    address: { "@type": "PostalAddress", streetAddress: BUSINESS_STREET, addressLocality: BUSINESS_LOCALITY, addressRegion: BUSINESS_REGION, postalCode: BUSINESS_POSTAL, addressCountry: BUSINESS_COUNTRY },
    geo: { "@type": "GeoCoordinates", latitude: 38.9417, longitude: -104.7458 },
    areaServed: [
      { "@type": "GeoCircle", geoMidpoint: { "@type": "GeoCoordinates", latitude: 38.9417, longitude: -104.7458 }, geoRadius: "96560" },
      ...COUNTIES.map((c) => ({ "@type": "AdministrativeArea", name: `${c.name}, Colorado` })),
      ...ALL_TOWNS.map((t) => ({ "@type": "City", name: `${t}, CO` })),
    ],
    priceRange: "$$",
  },
  {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
      { "@type": "ListItem", position: 2, name: "Colorado Springs Headquarters", item: `${SITE_URL}${PATH}` },
    ],
  },
].map((s) => ({ type: "application/ld+json", children: JSON.stringify(s) }));

export const Route = createFileRoute("/colorado-springs-headquarters")({
  head: () => seoHead(PATH, TITLE, DESC, "website", SCHEMA),
  component: HQPage,
});

function HQPage() {
  return (
    <ArticleLayout
      eyebrow="Colorado Springs Headquarters"
      title="Based in Colorado Springs. Serving businesses within 60 miles."
      intro="Colorado Web Impressions is run by Digital Marketing Coach Chris Heidlebaugh from Colorado Springs. With 27 years of experience, Chris builds websites, SEO, Google Maps and AI search visibility for local Colorado businesses — month to month, no locked contracts."
      breadcrumbs={[{ label: "Home", to: "/" }, { label: "Colorado Springs Headquarters" }]}
      path={PATH}
      faqs={[
        { q: "Where is Colorado Web Impressions located?", a: `Our office is at ${BUSINESS_ADDRESS_FULL}. Call ${BUSINESS_PHONE_DISPLAY}.` },
        { q: "What area do you serve?", a: "Businesses within about 60 miles of Colorado Springs: El Paso, Teller, Douglas, Pueblo and Fremont counties. We also work with businesses across Colorado remotely." },
        { q: "Can we meet in person?", a: "Yes. Businesses in the 60-mile area can meet Chris in person or by video, whichever is easier." },
        { q: "Do you lock clients into long contracts?", a: "No. Monthly marketing is month to month." },
      ]}
    >
      <h2>Our office</h2>
      <p>
        <strong>{BUSINESS_NAME}</strong><br />{BUSINESS_ADDRESS_FULL}<br />
        <a href={BUSINESS_PHONE_TEL}>{BUSINESS_PHONE_DISPLAY}</a>
      </p>
      <p><Link to="/colorado-springs-office-woodmen-rd">See our Google Business Profile, map and official listings</Link></p>

      <h2>Our 60-mile service area</h2>
      <div className="not-prose grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {COUNTIES.map((c) => (
          <div key={c.name} className="rounded-xl border border-border bg-card p-5">
            <h3 className="font-semibold text-foreground">{c.name}</h3>
            <ul className="mt-2 space-y-1 text-sm text-muted-foreground">
              {c.towns.map((t) => (
                <li key={t.label}>{t.to ? <Link to={t.to} className="text-primary underline-offset-2 hover:underline">{t.label}</Link> : t.label}</li>
              ))}
            </ul>
          </div>
        ))}
      </div>

      <h2>What we do from Colorado Springs</h2>
      <ul>
        {SERVICES.map((s) => <li key={s.to}><Link to={s.to}>{s.label}</Link></li>)}
        <li><Link to="/web-design-castle-rock-colorado">Castle Rock web design company</Link></li>
      </ul>

      <div className="not-prose my-8"><PricingTeaserStrip /></div>
      <RegionalHubLinks excludePath={PATH} />
    </ArticleLayout>
  );
}
